import React from 'react'
import {
  Box,
  Button,
  Col,
  Container,
  DialogContent,
  DialogTitle,
  Row,
  TextField,
  Typography,
  Radio,
  FormControlLabel,
  Paper,
  Link,
  Chip,
  TypoTooltip,
  IconButton,
  LoadingButton,
  DialogActionsWithStepper,
  Tooltip,
} from '@applift/factor'
import { Add, Close, Refresh } from '@applift/icons'

import { Action, PixelInfo, PixelInfoPayload } from '../../models/Pixel'
import { usePixelPiggybackTypeList } from '../../hooks/usePixel'
import {
  ALPHANUMERIC_UNDERSCORE_REGEX,
  EMPTY_SPACE_REGEX,
  MAX_CUSTOM_FIELD_COUNT,
  MAX_FINANCIAL_METRIC_COUNT,
  MAX_PIXEL_CUSTOMIZATION_CHARS,
  URL_REGEX,
} from '../../constants/constants'

import styles from './CreateOrEditPixel.module.scss'

interface PixelCustomizationProps {
  type: 'create' | 'edit'
  dispatch: React.Dispatch<Action>
  data: PixelInfo
  setStep: React.Dispatch<React.SetStateAction<number>>
  onCompletion: (data: PixelInfoPayload) => void
  isCompletionInProgress?: boolean
  initialPiggyBackDataRef?: React.MutableRefObject<{
    url: string
    type: number | null
  }>
  onClose: () => void
}

export const PixelCustomization = (props: PixelCustomizationProps) => {
  const {
    setStep,
    type,
    dispatch,
    data,
    onCompletion,
    isCompletionInProgress,
    initialPiggyBackDataRef,
    onClose,
  } = props

  const isCreateFlow = React.useMemo(() => type === 'create', [type])
  const isPiggyURLValidRef = React.useRef(true)

  const piggybackTypes = usePixelPiggybackTypeList()

  const [pigggybackURLVisibility, setPiggybackURLVisibility] =
    React.useState(false)
  const [customFieldVisibility, setCustomFieldVisibility] =
    React.useState(false)
  const [financialMetricVisibility, setFinancialMetricVisibility] =
    React.useState(false)
  const [currentCustomField, setCurrentCustomField] = React.useState('')

  const getActionBtnNameInCreate = () =>
    data.customFields.length || data.financialMetrics || data.piggybackData.url
      ? 'Create Pixel'
      : 'Skip & Create Pixel'
  const actionBtnNameInEdit = React.useRef('Next')

  const addCustomField = (field: string) => {
    dispatch({
      type: 'ADD_CUSTOM_FIELD',
      payload: field,
    })
    setCurrentCustomField('')
  }

  const getFieldError = (type: 'finance' | 'customField') => {
    let fieldError: string | null = null
    const field =
      type === 'finance' ? data.financialMetrics : currentCustomField
    if (!ALPHANUMERIC_UNDERSCORE_REGEX.test(field)) {
      fieldError = 'Only alphanumeric & underscores allowed'
    }
    if (field.length > MAX_PIXEL_CUSTOMIZATION_CHARS) {
      fieldError = 'Character limit exceeded'
    }
    return field.length ? fieldError : null
  }

  const checkIfCustomFieldAlreadyAdded = () =>
    data.customFields
      .map(item => item.replace(EMPTY_SPACE_REGEX, ''))
      .includes(currentCustomField.replace(EMPTY_SPACE_REGEX, ''))

  const getCustomFieldHelperText = () => {
    if (checkIfCustomFieldAlreadyAdded()) {
      return 'Custom field already added'
    }
    return getFieldError('customField') ? (
      <Typography variant="label" color="error">
        {getFieldError('customField')}
      </Typography>
    ) : null
  }

  const getFinancialMetrics = () =>
    isCreateFlow ? (
      <TextField
        label={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'between',
            }}
          >
            <Typography variant="label">Field Name</Typography>
            <Typography variant="label">
              {`${data.financialMetrics.length}/${MAX_PIXEL_CUSTOMIZATION_CHARS}`}
            </Typography>
          </Box>
        }
        variant="outlinedDash"
        placeholder="Enter field name"
        onChange={e =>
          e.target.value.trim() || e.target.value === ''
            ? dispatch({
                type: 'UPDATE_FINANCIAL_METRICS',
                payload: e.target.value,
              })
            : null
        }
        value={data.financialMetrics}
        error={!!getFieldError('finance')}
        helperText={
          getFieldError('finance') ? (
            <Typography variant="label" color="error">
              {getFieldError('finance')}
            </Typography>
          ) : null
        }
      />
    ) : (
      <Chip
        variant="filled"
        label={
          <TypoTooltip
            placement="top"
            arrow
            TypgraphyProps={{
              gutterBottom: false,
              sx: {
                display: 'block',
                textWeight: 'normal',
              },
            }}
          >
            {data.financialMetrics}
          </TypoTooltip>
        }
        color="secondary"
        sx={{ mb: 8, mr: 8 }}
      />
    )

  const validateJavascriptPixel = (htmlString: string) => {
    const parser = new DOMParser()
    const htmlDoc = parser.parseFromString(htmlString, 'text/html')
    const errorNode = htmlDoc.querySelector('parsererror')
    const headElement = htmlDoc?.children?.[0]?.children?.[0]

    const withoutBooleanAttributes = htmlString
      .replaceAll(/ (async|defer|nomodule)( )/g, ' ')
      .replaceAll(/ (async|defer|nomodule)>/g, '>')
    const parsedAsXml = parser.parseFromString(
      withoutBooleanAttributes,
      'application/xml'
    )
    const xmlParseError = parsedAsXml.querySelector('parsererror')

    return (
      !errorNode &&
      headElement?.children?.length === 1 &&
      headElement?.firstChild?.nodeName.toLowerCase() === 'script' &&
      URL_REGEX.test(
        Array.from(headElement?.children?.[0]?.attributes || []).find(
          attr => attr.nodeName === 'src'
        )?.nodeValue || ''
      ) &&
      !xmlParseError &&
      parsedAsXml.children?.length === 1 &&
      parsedAsXml.children?.[0]?.children?.length === 0 &&
      parsedAsXml.children?.[0]?.childNodes?.length === 0 &&
      htmlString.toLowerCase().includes('</script>')
    )
  }

  return (
    <>
      <DialogTitle onClose={onClose}>Advanced Pixel Customization</DialogTitle>
      <DialogContent dividers>
        <Container
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            bgColor: 'neutral-0',
          }}
        >
          <Row
            sx={{
              width: 100,
              mb: 24,
            }}
          >
            <Col>
              <Paper elevation={2} sx={{ width: 100, padding: 24 }}>
                <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                  <Typography
                    component="h5"
                    variant="bodySmall"
                    weight="demi"
                    sx={{ alignSelf: 'start' }}
                  >
                    Piggyback Pixel URL
                  </Typography>
                  {data.piggybackData.url ? (
                    <Tooltip
                      title="Reset the Piggyback Pixel URL"
                      placement="left"
                      arrow
                    >
                      <IconButton
                        onClick={() => {
                          setPiggybackURLVisibility(false)
                          dispatch({
                            type: 'UPDATE_PIGGYBACK_URL',
                            payload: initialPiggyBackDataRef?.current.url || '',
                          })
                          dispatch({
                            type: 'UPDATE_PIGGYBACK_TYPE',
                            payload: initialPiggyBackDataRef?.current.type || 1,
                          })
                        }}
                        color="hoverPrimary"
                      >
                        <Refresh
                          sx={{ textColor: 'primary-600' }}
                          fontSize={24}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>

                <Typography>
                  A piggyback pixel URL is an additional URL that can be added
                  to a conversion pixel to send conversion information to a
                  third-party platform.
                </Typography>
                <Box sx={{ mt: 8 }}>
                  {pigggybackURLVisibility || data.piggybackData.url ? (
                    <>
                      <TextField
                        sx={{ mt: 16, mb: 12 }}
                        fullWidth
                        label="Piggyback Pixel URL"
                        value={data.piggybackData.url}
                        variant="outlinedDash"
                        placeholder="Enter the piggyback pixel URL"
                        onChange={e => {
                          actionBtnNameInEdit.current = 'Save & Next'
                          isPiggyURLValidRef.current =
                            e.target.value === '' ||
                            (data.piggybackData.type === 2
                              ? validateJavascriptPixel(e.target.value)
                              : URL_REGEX.test(e.target.value.trim()))
                          e.target.value.trim() || e.target.value === ''
                            ? dispatch({
                                type: 'UPDATE_PIGGYBACK_URL',
                                payload: e.target.value,
                              })
                            : null
                        }}
                        helperText={
                          !isPiggyURLValidRef.current ? (
                            <Typography variant="label" color="error">
                              Please enter a valid{' '}
                              {data.piggybackData.type === 2
                                ? 'script tag'
                                : 'URL'}
                              . (e.g.{' '}
                              {data.piggybackData.type === 2
                                ? '<script src="https://www.iqm.com" async></script>'
                                : 'https://example.com'}
                              )
                            </Typography>
                          ) : null
                        }
                        error={!isPiggyURLValidRef.current}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ ml: 8, mr: 16 }}>
                          URL Type:
                        </Typography>
                        {piggybackTypes?.map(item => (
                          <Box key={item.id} sx={{ mr: 8 }}>
                            <FormControlLabel
                              onChange={() => {
                                actionBtnNameInEdit.current = 'Save & Next'
                                dispatch({
                                  type: 'UPDATE_PIGGYBACK_TYPE',
                                  payload: item.id,
                                })
                                dispatch({
                                  type: 'UPDATE_PIGGYBACK_URL',
                                  payload: '',
                                })
                                isPiggyURLValidRef.current = true
                              }}
                              checked={data.piggybackData.type === item.id}
                              control={<Radio />}
                              label={item.displayName}
                              labelPlacement="end"
                            />
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Link
                      sx={{
                        textWeight: 'demi',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      Component="button"
                      color="primary"
                      onClick={() => setPiggybackURLVisibility(true)}
                      className={styles.linkButton}
                    >
                      <Add fontSize={24} />
                      <Typography>Add</Typography>
                    </Link>
                  )}
                </Box>
              </Paper>
            </Col>
          </Row>
          <Row
            sx={{
              width: 100,
              mb: 24,
            }}
            xs={{ col: 2, gutterSize: 24, gutterDirection: 'x' }}
          >
            <Col>
              <Paper elevation={2} sx={{ width: 100, padding: 24 }}>
                <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                  <Typography
                    component="h5"
                    variant="bodySmall"
                    weight="demi"
                    sx={{ alignSelf: 'start' }}
                  >
                    {customFieldVisibility || data.customFields.length
                      ? `Custom Field (${data.customFields.length}/${MAX_CUSTOM_FIELD_COUNT})`
                      : 'Custom Field'}
                  </Typography>
                  {data.customFields.length && isCreateFlow ? (
                    <Tooltip
                      title="Reset the Custom Fields"
                      placement="right"
                      arrow
                    >
                      <IconButton
                        onClick={() => {
                          setCustomFieldVisibility(false)
                          setCurrentCustomField('')
                          dispatch({
                            type: 'RESET_CUSTOM_FIELD',
                          })
                        }}
                        color="hoverPrimary"
                      >
                        <Refresh
                          sx={{ textColor: 'primary-600' }}
                          fontSize={24}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
                <Typography>
                  Custom fields allow users to pass additional data along with
                  conversion details, which can then be used in reporting for
                  more detailed insights.
                </Typography>
                <Box sx={{ mt: 16 }}>
                  {customFieldVisibility || data.customFields.length ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isCreateFlow && (
                          <TextField
                            label={
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'between',
                                }}
                              >
                                <Typography variant="label">
                                  Field Name
                                </Typography>
                                <Typography variant="label">
                                  {`${currentCustomField.length}/${MAX_PIXEL_CUSTOMIZATION_CHARS}`}
                                </Typography>
                              </Box>
                            }
                            variant="outlinedDash"
                            placeholder="Enter field name"
                            onChange={e =>
                              e.target.value.trim() || e.target.value === ''
                                ? setCurrentCustomField(e.target.value)
                                : null
                            }
                            value={currentCustomField}
                            error={
                              !!(
                                getFieldError('customField') ||
                                checkIfCustomFieldAlreadyAdded()
                              )
                            }
                            helperText={
                              <Typography variant="label" color="error">
                                {getCustomFieldHelperText()}
                              </Typography>
                            }
                          />
                        )}
                        {(isCreateFlow &&
                          data.customFields.length < MAX_CUSTOM_FIELD_COUNT) ||
                        (!isCreateFlow && !data.customFields.length) ? (
                          <>
                            <Link
                              sx={{ textWeight: 'demi', mr: 8, mt: 16 }}
                              Component="button"
                              color="primary"
                              onClick={() => {
                                if (currentCustomField.length) {
                                  addCustomField(currentCustomField)
                                }
                              }}
                              disabled={
                                !currentCustomField.length ||
                                !isCreateFlow ||
                                !!getFieldError('customField') ||
                                checkIfCustomFieldAlreadyAdded()
                              }
                              className={styles.linkButton}
                            >
                              <Add fontSize={24} />
                              <Typography>Add</Typography>
                            </Link>
                          </>
                        ) : null}
                      </Box>

                      <Box sx={{ mt: 8 }}>
                        {data.customFields.map(field => (
                          <Chip
                            key={field}
                            variant="filled"
                            clickable={isCreateFlow}
                            deleteIcon={
                              isCreateFlow ? (
                                <Close
                                  fontSize={16}
                                  sx={{ textColor: 'neutral-400' }}
                                />
                              ) : undefined
                            }
                            label={
                              <TypoTooltip
                                placement="top"
                                arrow
                                TypgraphyProps={{
                                  gutterBottom: false,
                                  sx: {
                                    display: 'block',
                                    textWeight: 'normal',
                                  },
                                }}
                              >
                                {field}
                              </TypoTooltip>
                            }
                            onDelete={
                              isCreateFlow
                                ? () =>
                                    dispatch({
                                      type: 'REMOVE_CUSTOM_FIELD',
                                      payload: field,
                                    })
                                : undefined
                            }
                            color="secondary"
                            sx={{ mb: 8, mr: 8 }}
                          />
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Link
                      sx={{ textWeight: 'demi', mr: 8 }}
                      Component="button"
                      color="primary"
                      onClick={() => setCustomFieldVisibility(true)}
                      disabled={!isCreateFlow}
                      className={styles.linkButton}
                    >
                      <Add fontSize={24} />
                      <Typography>Add</Typography>
                    </Link>
                  )}
                </Box>
              </Paper>
            </Col>

            <Col>
              <Paper elevation={2} sx={{ width: 100, padding: 24 }}>
                <Box sx={{ display: 'flex', justifyContent: 'between' }}>
                  <Typography
                    component="h5"
                    variant="bodySmall"
                    weight="demi"
                    sx={{ alignSelf: 'start' }}
                  >
                    {!financialMetricVisibility && !data.financialMetrics
                      ? 'Financial Metric'
                      : `Financial Metric (${
                          data.financialMetrics.length > 0
                            ? MAX_FINANCIAL_METRIC_COUNT
                            : 0
                        }/${MAX_FINANCIAL_METRIC_COUNT})`}
                  </Typography>
                  {data.financialMetrics && isCreateFlow ? (
                    <Tooltip
                      title="Reset the Financial Metric"
                      placement="left"
                      arrow
                    >
                      <IconButton
                        onClick={() => {
                          setFinancialMetricVisibility(false)
                          dispatch({
                            type: 'UPDATE_FINANCIAL_METRICS',
                            payload: '',
                          })
                        }}
                      >
                        <Refresh
                          sx={{ textColor: 'primary-600' }}
                          fontSize={24}
                        />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>

                <Typography>
                  It allows users to pass additional financial data along with
                  conversion details, which can then be used to calculate ROAS.
                </Typography>
                <Box sx={{ mt: 16 }}>
                  {financialMetricVisibility || data.financialMetrics ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getFinancialMetrics()}
                    </Box>
                  ) : (
                    <Link
                      sx={{ textWeight: 'demi', mr: 8 }}
                      Component="button"
                      disabled={!isCreateFlow}
                      color="primary"
                      onClick={() => setFinancialMetricVisibility(true)}
                      className={styles.linkButton}
                    >
                      <Add fontSize={24} />
                      <Typography>Add</Typography>
                    </Link>
                  )}
                </Box>
              </Paper>
            </Col>
          </Row>
        </Container>
      </DialogContent>

      <DialogActionsWithStepper maxSteps={3} activeStep={1}>
        <Button color="secondary" onClick={() => setStep(1)}>
          Previous
        </Button>
        <LoadingButton
          onClick={() => onCompletion(data)}
          loading={isCompletionInProgress}
          variant="contained"
          disabled={
            !isPiggyURLValidRef.current ||
            !!getFieldError('finance') ||
            !!getFieldError('customField')
          }
        >
          {isCreateFlow
            ? getActionBtnNameInCreate()
            : actionBtnNameInEdit.current}
        </LoadingButton>
      </DialogActionsWithStepper>
    </>
  )
}
